import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, withRouter } from 'react-router-dom';

import { EditOutlined, EyeOutlined } from '@ant-design/icons';
import { Button, Select, Space } from 'antd';
import _ from 'lodash';
import PropTypes from 'prop-types';
import queryString from 'query-string';

import { Loading, PageHeader } from '~/components';
import { decryptObject } from '~/helpers/functions';
import { setStorage, getStorage } from '~/helpers/storage';

import getTemplates from './actions';
import S from './styles';
import VisualizarTemplate from './VisualizarTemplate';

const { Option } = Select;

const getTemplatePreview = (template) => {
  if (!template || typeof template !== 'string') return '';

  if (typeof window === 'undefined' || !window.DOMParser) return template;

  const doc = new DOMParser().parseFromString(template, 'text/html');
  return doc.body?.innerHTML || template;
};

const decryptFiltroUrl = (encryptedFiltro, location) => {
  const candidates = [encryptedFiltro, encryptedFiltro?.replace(/ /g, '+')].filter(Boolean);
  let lastError = null;

  for (const candidate of [...new Set(candidates)]) {
    try {
      return decryptObject(candidate);
    } catch (error) {
      lastError = error;
    }
  }

  // Mantem contexto suficiente para diagnosticar problemas de encoding em producao.
  // eslint-disable-next-line no-console
  console.error('Erro ao descriptografar filtro da URL', {
    href: typeof window !== 'undefined' ? window.location.href : '',
    searchLength: location.search?.length,
    parsedDLength: encryptedFiltro?.length,
    parsedDStart: encryptedFiltro?.slice(0, 16),
    parsedDEnd: encryptedFiltro?.slice(-16),
    hasSpace: encryptedFiltro?.includes(' '),
    hasPlus: encryptedFiltro?.includes('+'),
    hasEncodedPlus: location.search?.includes('%2B'),
    errorName: lastError?.name,
    errorMessage: lastError?.message,
  });

  return null;
};

function TipoTemplate({ location }) {
  const history = useHistory();

  const [data, setData] = useState([]);
  const [itemSelected, setItemSelected] = useState({});
  const [loading, setLoading] = useState({ active: false, text: '' });
  const [filtro, setFiltro] = useState({ modulo: '', cnpj: '', tipo: '' });

  const doGetTemplates = async () => {
    setLoading({ active: true, text: 'Aguarde enquanto buscamos os templates' });

    await getTemplates(filtro)
      .then((response) => {
        let newData = response || [];

        if (newData.length) {
          newData = response.map((item) => {
            return {
              ...item,
              title: item.nome,
            };
          });
        }

        setData(newData);
        setItemSelected({});
      })
      .finally(() => setLoading({ active: false, text: '' }));
  };

  const verificarFiltroRota = () => {
    const searchUrl = queryString.parse(location.search);
    // TODO: Ajustar caso necessário
    // Remove a querystring da url
    // history.push(location.pathname);
    // Trata a querystring como filtro
    if (searchUrl && searchUrl.d) {
      const filtroUrl = decryptFiltroUrl(searchUrl.d, location);

      if (filtroUrl) {
        const newFiltro = { ...filtro };
        Object.keys(filtroUrl).forEach((key) => {
          if (newFiltro[key] !== filtroUrl[key]) {
            newFiltro[key] = filtroUrl[key];
          }
        });

        if (!_.isEqual(newFiltro, filtro)) {
          setFiltro(newFiltro);
        }
        setStorage('filtro-tipo-template', newFiltro, 'session');
      }
    } else {
      const filtroStorage = getStorage('filtro-tipo-template', 'session');
      if (filtroStorage) {
        try {
          const newFiltro = filtroStorage;
          if (!_.isEqual(newFiltro, filtro)) {
            setFiltro(newFiltro);
          }
        } catch (error) {
          /* Ignora o erro de leitura do localStorage */
        }
      }
    }
  };

  const initialRender = useCallback(() => {
    verificarFiltroRota();
    if (filtro && filtro.cnpj) {
      doGetTemplates();
    }
  }, [filtro]); // eslint-disable-line

  useEffect(() => {
    initialRender();
  }, [initialRender]);

  const selecionarItem = (item) => {
    const temp = { ...item, id_Template: item.id, id: null, credito: filtro.credito };

    setItemSelected(temp);
    setStorage('contrato', temp, 'session');
    setStorage('filtro-tipo-template', filtro, 'session');
    history.push('/novo/informar-dados');
  };

  const doTipoOnChange = (tipo) => {
    const newFiltro = { ...filtro, tipo };
    setStorage('filtro-tipo-template', newFiltro, 'session');
    setFiltro(newFiltro);
  };

  return (
    <>
      <Loading active={loading.active} text={loading.text} />

      <VisualizarTemplate
        template={itemSelected.template}
        title={itemSelected.nome}
        visible={!_.isEmpty(itemSelected)}
        onCancel={() => setItemSelected({})}
        onOk={() => selecionarItem(itemSelected)}
      />

      <PageHeader
        title="Escolher um template"
        extra={
          <>
            <Select value={filtro.tipo} onChange={doTipoOnChange}>
              <Option value="">Todos os tipos</Option>
              <Option value="contrato">Contrato</Option>
              <Option value="proposta">Proposta</Option>
            </Select>
            <Button danger onClick={() => history.push('/home')}>
              Cancelar
            </Button>
          </>
        }
      />

      <S.ListPaper>
        {data.map((item) => (
          <S.Item key={item.id}>
            <S.Paper>
              <S.Overlay>
                <Space direction="vertical" size="middle" align="center">
                  <Button ghost icon={<EyeOutlined />} onClick={() => setItemSelected(item)}>
                    Visualizar
                  </Button>
                  <Button ghost icon={<EditOutlined />} onClick={() => selecionarItem(item)}>
                    Selecionar
                  </Button>
                </Space>
              </S.Overlay>

              <S.Paper.Content
                dangerouslySetInnerHTML={{ __html: getTemplatePreview(item.template) }}
              />
            </S.Paper>
            <S.Title>{item.title}</S.Title>
          </S.Item>
        ))}
      </S.ListPaper>
    </>
  );
}

TipoTemplate.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
  }),
};

TipoTemplate.defaultProps = {
  location: {
    pathname: '',
    search: '',
  },
};

export default withRouter(TipoTemplate);
